import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, PieChart, Pie, Legend, LineChart, Line } from 'recharts';
import SupplierProducts from './SupplierProducts';
import { exportReportHtml, exportReportPdf, exportReportXlsx, exportReportBackend, svgBarsH, svgLine, svgDonut, stripHtml } from '../utils/reportExport';
import './SupplierCockpit.css';

const API = import.meta.env.VITE_API_URL;
const BAR_COLORS = ['#1A8AAF', '#0D2B3E', '#2FA4C7', '#1D8A5E', '#D98323', '#6B8299', '#8E5AA8'];
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);
const titleCase = (s) => String(s || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// classify a (Dutch) dosage form to a route of administration
function routeOfForm(form) {
  const f = String(form || '').toLowerCase();
  if (/(zetpil|rectaal|klysma|klysma|suppos)/.test(f)) return 'rectal';
  if (/(infus|injec|inject|intraveneus|ampul|parenteraal|\biv\b)/.test(f)) return 'intravenous';
  if (/(tablet|capsule|caps|drank|stroop|siroop|granulaat|poeder|sachet|dragee|zuig|smelt|kauw|bruis|omhuld|druppel|zuigtablet|lozenge)/.test(f)) return 'oral';
  return 'other';
}
const routeMatch = (routeChip, form) => {
  const r = String(routeChip || '').toLowerCase();
  const fr = routeOfForm(form);
  if (fr === r) return true;
  if (fr === 'intravenous' && /(intraven|parenter|infus|inject)/.test(r)) return true;
  return false;
};

function AiBlock({ title, text, loading }) {
  return (
    <div className="cok-ai">
      <div className="cok-ai-head"><span className="cok-ai-badge">✦</span>{title}</div>
      {loading
        ? <div className="cok-ai-body loading">Generating from the data…</div>
        : <div className="cok-ai-body nova-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text || 'Not available.', { ALLOWED_TAGS: ['p', 'ul', 'ol', 'li', 'strong', 'em', 'br'], ALLOWED_ATTR: [] }) }} />}
    </div>
  );
}

const eur = (n) => (n || n === 0) ? '€' + Number(n).toLocaleString('en-US') : '—';
const num = (n) => (n || n === 0) ? Number(n).toLocaleString('en-US') : '—';
const comingSoon = <span className="cok-badge" style={{ background: '#EDF1F7', color: 'var(--t3)' }}>Coming soon</span>;

function Html({ html, loading, empty }) {
  if (loading) return <div className="cok-muted">Generating…</div>;
  if (!html) return <div className="cok-muted">{empty || 'Not available.'}</div>;
  return <div className="nova-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, { ALLOWED_TAGS: ['p', 'ul', 'ol', 'li', 'strong', 'em', 'br'], ALLOWED_ATTR: [] }) }} />;
}
function KV({ rows }) {
  return (<table className="cok-table"><tbody>{rows.map((r, i) => (<tr key={i}><td style={{ fontWeight: 600, color: 'var(--navy)', width: '46%' }}>{r.k}</td><td>{r.v}</td></tr>))}</tbody></table>);
}
function PieMini({ data, colors, height = 210 }) {
  const d = (data || []).filter((x) => x.value > 0);
  if (!d.length) return <div className="cok-muted">No data.</div>;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={d} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={78} paddingAngle={2}>
          {d.map((_, i) => <Cell key={i} fill={(colors || BAR_COLORS)[i % (colors || BAR_COLORS).length]} />)}
        </Pie>
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--brd)' }} /><Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}
function MiniBar({ data, height = 240 }) {
  if (!data || !data.length) return <div className="cok-muted">No data.</div>;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 40, left: -14 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
        <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-22} textAnchor="end" height={64} />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--brd)' }} />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>{data.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}</Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function MoleculeCockpit() {
  const { name } = useParams();
  const molecule = decodeURIComponent(name || '');
  const navigate = useNavigate();

  const [kpis, setKpis] = useState(null);
  const [forms, setForms] = useState(null);
  const [pref, setPref] = useState(null);
  const [prefQ, setPrefQ] = useState('');
  const [hist, setHist] = useState(null);
  const [cbg, setCbg] = useState(null);
  const [hma, setHma] = useState(null);
  const [fk, setFk] = useState(null);
  const [fkDetail, setFkDetail] = useState(null);
  const [fkDetailPath, setFkDetailPath] = useState(null);
  const [fkDetailLoading, setFkDetailLoading] = useState(false);
  const [fkDetailError, setFkDetailError] = useState(null);
  const [fkDetailBlocked, setFkDetailBlocked] = useState(false);
  const [fkOpenSections, setFkOpenSections] = useState({});
  const [fkLang, setFkLang] = useState('nl'); // 'nl' original | 'en' translated
  const [fkEn, setFkEn] = useState(null);
  const [fkTranslating, setFkTranslating] = useState(false);
  const fkPollRef = useRef(null);
  const fkPollCount = useRef(0);
  const [mach, setMach] = useState(null);
  const [tree, setTree] = useState(null);
  const [selGroup, setSelGroup] = useState(null);
  const [selForm, setSelForm] = useState(null);
  const [selStrength, setSelStrength] = useState(null);
  const [selPack, setSelPack] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [competitor, setCompetitor] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiStatus, setAiStatus] = useState('loading'); // loading | ready
  const [err, setErr] = useState(null);
  const [phase, setPhase] = useState('intro'); // intro | loading | fading | done
  const [progress, setProgress] = useState(3);
  const [selRoute, setSelRoute] = useState(null);
  const progressRef = useRef(3);
  const fadeLatch = useRef(false);

  // Resolve the typed name to the real INN present in the data (handles cross-language
  // spellings, e.g. Thiamazole ⇄ Thiamazol, Paracétamol, acetaminophen). If it maps to a
  // different stored name, redirect to it so every panel queries the correct molecule.
  useEffect(() => {
    let alive = true;
    if (!molecule) return () => { alive = false; };
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/resolve?name=${encodeURIComponent(molecule)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (alive && d.resolved && d.resolved.toLowerCase() !== molecule.toLowerCase()) {
          navigate(`/cockpit/molecule/${encodeURIComponent(d.resolved)}`, { replace: true });
        }
      } catch { /* leave the typed name as-is */ }
    })();
    return () => { alive = false; };
  }, [molecule, navigate]);

  // fast KPIs
  useEffect(() => {
    let alive = true; setErr(null); setKpis(null);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/cockpit-kpis?molecule=${encodeURIComponent(molecule)}`);
        if (!r.ok) throw new Error('kpis');
        const d = await r.json(); if (alive) setKpis(d);
      } catch { if (alive) setErr('Could not load molecule data.'); }
    })();
    return () => { alive = false; };
  }, [molecule]);

  // per-form data
  useEffect(() => {
    let alive = true; setForms(null);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/cockpit-forms?molecule=${encodeURIComponent(molecule)}`);
        if (r.ok) { const d = await r.json(); if (alive) setForms(d); }
        else if (alive) setForms({ forms: [] });
      } catch { if (alive) setForms({ forms: [] }); }
    })();
    return () => { alive = false; };
  }, [molecule]);

  // drill-down tree (Group -> form -> strength)
  useEffect(() => {
    let alive = true; setTree(null); setSelGroup(null); setSelForm(null); setSelStrength(null); setSelPack(null);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/cockpit-tree?molecule=${encodeURIComponent(molecule)}`);
        if (r.ok) { const d = await r.json(); if (alive) setTree(d); }
        else if (alive) setTree({ groups: [] });
      } catch { if (alive) setTree({ groups: [] }); }
    })();
    return () => { alive = false; };
  }, [molecule]);

  // cards for the current selection
  useEffect(() => {
    let alive = true; setAnalyticsLoading(true);
    const params = new URLSearchParams({ molecule });
    if (selGroup) params.set('group', selGroup);
    if (selForm) params.set('form', selForm);
    if (selStrength) params.set('strength', selStrength);
    if (selPack) params.set('pack', selPack);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/cockpit-analytics?${params.toString()}`);
        if (r.ok) { const d = await r.json(); if (alive) setAnalytics(d); }
        else if (alive) setAnalytics(null);
      } catch { if (alive) setAnalytics(null); }
      finally { if (alive) setAnalyticsLoading(false); }
    })();
    return () => { alive = false; };
  }, [molecule, selGroup, selForm, selStrength, selPack]);

  // current-year MACH (recomputes with the selected form/strength)
  useEffect(() => {
    let alive = true; setMach(null);
    const params = new URLSearchParams({ molecule });
    if (selForm) params.set('form', selForm);
    if (selStrength) params.set('strength', selStrength);
    if (selPack) params.set('pack', selPack);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/cockpit-mach?${params.toString()}`);
        if (r.ok) { const d = await r.json(); if (alive) setMach(d); }
        else if (alive) setMach({ rows: [] });
      } catch { if (alive) setMach({ rows: [] }); }
    })();
    return () => { alive = false; };
  }, [molecule, selForm, selStrength, selPack]);

  // historical (recomputes with the selected form/strength)
  useEffect(() => {
    let alive = true; setHist(null);
    const params = new URLSearchParams({ molecule });
    if (selForm) params.set('form', selForm);
    if (selStrength) params.set('strength', selStrength);
    if (selPack) params.set('pack', selPack);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/cockpit-historical?${params.toString()}`);
        if (r.ok) { const d = await r.json(); if (alive) setHist(d); }
        else if (alive) setHist({ years: [] });
      } catch { if (alive) setHist({ years: [] }); }
    })();
    return () => { alive = false; };
  }, [molecule, selForm, selStrength, selPack]);

  // Competitor analysis (Farminform vs Z-Index), reacts to the drill-down selection
  useEffect(() => {
    let alive = true; setCompetitor(null);
    const params = new URLSearchParams({ molecule });
    if (selGroup) params.set('group', selGroup);
    if (selForm) params.set('form', selForm);
    if (selStrength) params.set('strength', selStrength);
    if (selPack) params.set('pack', selPack);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/cockpit-competitor?${params.toString()}`);
        if (r.ok) { const d = await r.json(); if (alive) setCompetitor(d); }
        else if (alive) setCompetitor({ newMAs: [], removedMAs: [], common: [], price: {} });
      } catch { if (alive) setCompetitor({ newMAs: [], removedMAs: [], common: [], price: {} }); }
    })();
    return () => { alive = false; };
  }, [molecule, selGroup, selForm, selStrength, selPack]);

  // CBG registered products (medicines registration board)
  useEffect(() => {
    let alive = true; setCbg(null);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/cbg?molecule=${encodeURIComponent(molecule)}`);
        if (r.ok) { const d = await r.json(); if (alive) setCbg(d); }
        else if (alive) setCbg({ rows: [], total: 0 });
      } catch { if (alive) setCbg({ rows: [], total: 0 }); }
    })();
    return () => { alive = false; };
  }, [molecule]);

  // HMA (MRI product index) — cross-language match
  useEffect(() => {
    let alive = true; setHma(null);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/hma?molecule=${encodeURIComponent(molecule)}`);
        if (r.ok) { const d = await r.json(); if (alive) setHma(d); }
        else if (alive) setHma({ rows: [], total: 0 });
      } catch { if (alive) setHma({ rows: [], total: 0 }); }
    })();
    return () => { alive = false; };
  }, [molecule]);

  // FK (Farmacotherapeutisch Kompas) records — live search by molecule
  useEffect(() => {
    let alive = true; setFk(null);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/fk/search?q=${encodeURIComponent(molecule)}`);
        if (r.ok) { const d = await r.json(); if (alive) setFk(d); }
        else if (alive) setFk({ results: [], blocked: false });
      } catch { if (alive) setFk({ results: [], blocked: false }); }
    })();
    return () => { alive = false; };
  }, [molecule]);

  // FK detail (click a medicine name → fetch + translate, with polling)
  const stopFkPolling = useCallback(() => {
    if (fkPollRef.current) { clearTimeout(fkPollRef.current); fkPollRef.current = null; }
    fkPollCount.current = 0;
  }, []);

  const closeFkDetail = useCallback(() => {
    setFkDetail(null); setFkDetailPath(null); setFkDetailError(null); setFkDetailBlocked(false); setFkOpenSections({});
    setFkLang('nl'); setFkEn(null); setFkTranslating(false); stopFkPolling();
  }, [stopFkPolling]);

  // Open a medicine → show the ORIGINAL Dutch immediately (no translation).
  const openFkDetail = useCallback((fkPath) => {
    if (fkDetailPath === fkPath) { closeFkDetail(); return; }
    setFkDetail(null); setFkDetailPath(fkPath); setFkDetailLoading(true); setFkDetailError(null); setFkDetailBlocked(false);
    setFkOpenSections({}); setFkLang('nl'); setFkEn(null); setFkTranslating(false); stopFkPolling();
    (async () => {
      try {
        const res = await fetch(`${API}/api/market-intelligence/fk/detail-nl?path=${encodeURIComponent(fkPath)}`);
        const body = await res.json().catch(() => ({}));
        if (body.blocked) { setFkDetailBlocked(true); setFkDetailLoading(false); return; }
        if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
        setFkDetail(body); setFkDetailLoading(false);
      } catch (err) { setFkDetailError(err.message); setFkDetailLoading(false); }
    })();
  }, [fkDetailPath, closeFkDetail, stopFkPolling]);

  // On demand: translate the open medicine to English (existing translate+poll flow).
  const translateFkToEnglish = useCallback(() => {
    if (fkEn) { setFkLang('en'); return; }        // already fetched
    if (!fkDetailPath) return;
    setFkTranslating(true); stopFkPolling(); fkPollCount.current = 0;
    const fetchOnce = async () => {
      const res = await fetch(`${API}/api/market-intelligence/fk/detail?path=${encodeURIComponent(fkDetailPath)}`);
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `HTTP ${res.status}`); }
      return res.json();
    };
    const poll = async () => {
      try {
        const data = await fetchOnce();
        if (data.status === 'ready') { setFkEn(data); setFkLang('en'); setFkTranslating(false); stopFkPolling(); }
        else if (data.status === 'translating') {
          fkPollCount.current++;
          if (fkPollCount.current > 60) { setFkTranslating(false); stopFkPolling(); }
          else { fkPollRef.current = setTimeout(poll, 3000); }
        } else { setFkTranslating(false); }
      } catch { setFkTranslating(false); stopFkPolling(); }
    };
    poll();
  }, [fkEn, fkDetailPath, stopFkPolling]);

  const toggleFkSection = (i) => setFkOpenSections((prev) => ({ ...prev, [i]: !prev[i] }));

  // NL preference policy
  useEffect(() => {
    let alive = true; setPref(null);
    (async () => {
      try {
        const r = await fetch(`${API}/api/market-intelligence/molecule/preferentiebeleid?molecule=${encodeURIComponent(molecule)}`);
        if (r.ok) { const d = await r.json(); if (alive) setPref(d); }
        else if (alive) setPref({ available: false, products: [], insurers: [] });
      } catch { if (alive) setPref({ available: false, products: [], insurers: [] }); }
    })();
    return () => { alive = false; };
  }, [molecule]);

  // Preferred product per insurer, from the preference cluster: formatted "1 - <article>".
  const prefName = (label) => {
    if (!pref || !pref.available || !pref.preferent) return '—';
    const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, '');
    const ins = (pref.insurers || []).find((x) => norm(x.label) === norm(label));
    if (!ins) return '—';
    const names = pref.preferent[ins.key] || [];
    if (!names.length) return '—';
    return names.map((n) => `1 - ${n}`).join('; ');
  };

  // async AI (poll)
  useEffect(() => {
    let alive = true;
    (async () => {
      setAiStatus('loading'); setAi(null);
      for (let i = 0; i < 40 && alive; i++) {
        try {
          const r = await fetch(`${API}/api/market-intelligence/molecule/cockpit-ai?molecule=${encodeURIComponent(molecule)}`);
          if (r.ok) { const d = await r.json(); if (d.status === 'ready') { if (alive) { setAi(d); setAiStatus('ready'); } return; } }
        } catch { /* retry */ }
        await new Promise((res) => setTimeout(res, 3000));
      }
      if (alive) setAiStatus('ready');
    })();
    return () => { alive = false; };
  }, [molecule]);

  // intro (≤2s) then real progress gated on KPIs + AI
  useEffect(() => {
    const toLoading = setTimeout(() => setPhase((p) => (p === 'intro' ? 'loading' : p)), 2000);
    const failsafe = setTimeout(() => setPhase('done'), 120000);
    return () => { clearTimeout(toLoading); clearTimeout(failsafe); };
  }, []);
  useEffect(() => {
    if (phase === 'done' || phase === 'fading') return undefined;
    const id = setInterval(() => {
      const kpiReady = !!kpis || !!err;
      const formsReady = !!forms;
      const prefReady = !!pref;
      const aiReady = aiStatus === 'ready';
      const all = kpiReady && formsReady && prefReady && aiReady;
      const target = 6 + (kpiReady ? 28 : 0) + (formsReady ? 22 : 0) + (prefReady ? 18 : 0) + (aiReady ? 25 : 0);
      const cap = (all && phase === 'loading') ? 100 : Math.min(target, 99);
      setProgress((prev) => { const v = prev >= cap ? cap : Math.min(cap, prev + Math.max(0.5, (cap - prev) * 0.06)); progressRef.current = v; return v; });
      if (all && phase === 'loading' && progressRef.current >= 99.9 && !fadeLatch.current) {
        fadeLatch.current = true; setPhase('fading'); setTimeout(() => setPhase('done'), 450);
      }
    }, 90);
    return () => clearInterval(id);
  }, [phase, kpis, forms, pref, err, aiStatus]);

  const yn = useCallback((v) => (v === 'Yes' ? 'green' : 'red'), []);
  const atcLine = kpis && kpis.atc ? `${kpis.atc.code || '—'}  ·  ${kpis.atc.category || '—'}  ·  ${kpis.atc.classification || '—'}` : '';

  if (err && !kpis) return <div className="cok-page"><div className="cok-error">{err} <button className="cok-filter-reset" onClick={() => navigate(-1)}>Go back</button></div></div>;

  const k = kpis || {};

  // Build the export model from what is currently on the page (same content as displayed).
  function buildMoleculeReport() {
    const a = analytics || {}, c = competitor || {}, pr = c.price || {};
    const sel = [selGroup, selForm && titleCase(selForm), selStrength, selPack && `pack ${selPack}`].filter(Boolean).join(' › ') || 'All forms';
    const atc = k.atc || {};
    const sections = [];
    sections.push({ heading: 'Overview', kpis: [
      { label: 'Suppliers', value: k.suppliers ?? '—' }, { label: 'Dosage forms', value: k.dosageForms ?? '—' },
      { label: 'Type', value: k.moleculeType ?? '—' }, { label: 'NL available', value: k.nlAvailable ?? '—' },
      { label: 'NL MA', value: k.nlMA ?? '—' }, { label: 'EU available', value: k.euAvailable ?? '—' },
      { label: 'Switch', value: k.switch ?? '—' }, { label: 'Contracts', value: k.contracts ?? '—' },
    ] });
    sections.push({ heading: 'Summary', kv: [
      { k: 'ATC code', v: atc.code || '—' }, { k: 'ATC category', v: atc.category || '—' }, { k: 'ATC classification', v: atc.classification || '—' },
    ], charts: (k.dosageFormCounts && k.dosageFormCounts.length) ? [{ title: 'Market presence by dosage form', svg: svgBarsH(k.dosageFormCounts) }] : [],
      notes: [ai && ai.capability ? `Capability: ${stripHtml(ai.capability)}` : '', ai && ai.executiveSummary ? stripHtml(ai.executiveSummary) : ''].filter(Boolean) });
    sections.push({ heading: `Market — ${sel}`, kpis: [
      { label: 'Market value', value: eur(a.marketValue) }, { label: 'Market volume', value: num(a.marketVolume) },
      { label: 'Highest AIP', value: a.highestAIP != null ? eur(a.highestAIP) : '—' }, { label: 'Lowest AIP', value: a.lowestAIP != null ? eur(a.lowestAIP) : '—' },
      { label: 'Retail %', value: a.retailPct != null ? `${a.retailPct}%` : '—' }, { label: 'Hospital %', value: a.hospitalPct != null ? `${a.hospitalPct}%` : '—' },
    ], charts: (a.retailPct != null) ? [{ title: 'Retail vs hospital', svg: svgDonut([{ name: 'Retail', value: a.retailPct, color: '1A8AAF' }, { name: 'Hospital', value: a.hospitalPct, color: '0D2B3E' }]) }] : [] });
    sections.push({ heading: 'Competitor analysis', kpis: [
      { label: 'New MAs', value: c.newMAsCount || 0 }, { label: 'Removed MAs', value: c.removedMAsCount || 0 }, { label: 'In both', value: c.commonCount || 0 },
      { label: 'Price / unit (curr)', value: pr.current != null ? eur(pr.current) : '—' }, { label: 'Price / unit (prev)', value: pr.previous != null ? eur(pr.previous) : '—' },
      { label: 'Change', value: pr.pctChange != null ? `${pr.pctChange > 0 ? '+' : ''}${pr.pctChange}%` : '—' },
    ], tables: [
      { title: `New records / new MAs (${(c.newMAs || []).length})`, cols: [{ key: 'name', label: 'Company' }], rows: (c.newMAs || []).map((x) => ({ name: x })) },
      { title: `Cancelled / removed MAs (${(c.removedMAs || []).length})`, cols: [{ key: 'name', label: 'Company' }], rows: (c.removedMAs || []).map((x) => ({ name: x })) },
    ] });
    if (mach && mach.rows && mach.rows.length) sections.push({ heading: 'MACH data — current year', tables: [{ cols: [
      { key: 'form', label: 'Form' }, { key: 'strength', label: 'Strength' }, { key: 'unit', label: 'Unit' }, { key: 'pack', label: 'Pack' },
      { key: 'psf', label: 'PSF (€)' }, { key: 'cu', label: 'CU' }, { key: 'unitv', label: 'Unit' }, { key: 'dot', label: 'DOT' },
    ], rows: mach.rows }] });
    if (hist && hist.years && hist.years.length) {
      const years = [...hist.years].sort((x, y) => String(x.year).localeCompare(String(y.year)));
      sections.push({ heading: 'Historical data (MAT by year)', charts: [{ title: 'PSF value by year', svg: svgLine(years.map((y) => ({ label: y.year, psf: y.psf || 0 })), { key: 'psf', area: true }) }],
        tables: [{ cols: [{ key: 'year', label: 'Year' }, { key: 'psf', label: 'PSF (€)' }, { key: 'cu', label: 'CU' }, { key: 'unit', label: 'Unit' }, { key: 'dot', label: 'DOT' }], rows: [...hist.years].sort((x, y) => String(y.year).localeCompare(String(x.year))) }] });
    }
    if (cbg && cbg.rows && cbg.rows.length) sections.push({ heading: 'CBG Data', tables: [{ cols: [
      { key: 'ma_number', label: 'MA number' }, { key: 'product_name', label: 'Product' }, { key: 'atc', label: 'ATC' }, { key: 'active_substance', label: 'Active substance' },
    ], rows: cbg.rows }] });
    if (pref && pref.available && pref.products && pref.products.length) sections.push({ heading: 'NL market — preference policy', tables: [{ cols: [
      { key: 'article', label: 'Article' }, { key: 'responsible', label: 'Responsible' }, { key: 'pack', label: 'Pack' },
    ], rows: pref.products }] });
    return {
      title: titleCase(molecule), subtitle: 'Molecule Cockpit',
      meta: [{ k: 'ATC', v: atc.code || '—' }, { k: 'Type', v: k.moleculeType || '—' }, { k: 'Suppliers', v: k.suppliers ?? '—' }, { k: 'Switch', v: k.switch || '—' }],
      sections,
    };
  }

  const handleDownload = async (fmt) => {
    try {
      const model = buildMoleculeReport();
      if (fmt === 'HTML') { exportReportHtml(model); return; }
      if (fmt === 'PDF') { exportReportPdf(model); return; }
      if (fmt === 'XLSX') { exportReportXlsx(model); return; }
      await exportReportBackend(fmt.toLowerCase(), model);
    } catch { /* ignore */ }
  };

  return (
   <>
    {phase !== 'done' && (
      <div className={`cok-intro${phase === 'fading' ? ' fade' : ''}`}>
        <div className="cok-intro-welcome">Welcome to the Molecule Cockpit</div>
        <div className="cok-intro-sub">{molecule}</div>
        {phase !== 'intro' && (
          <div className="cok-intro-barwrap">
            <div className="cok-intro-bar"><div className="cok-intro-fill" style={{ width: `${Math.round(progress)}%` }} /></div>
            <div className="cok-intro-pct">{Math.round(progress)}% · preparing your cockpit</div>
          </div>
        )}
      </div>
    )}
    <div className="cok-page">
      {/* Hero */}
      <div className="cok-hero">
        <div className="cok-welcome">Welcome to the Molecule Cockpit</div>
        <h1>{molecule}</h1>
        <div className="cok-sub">{atcLine || 'Market intelligence'}</div>
        <div className="cok-actions">
          {['PDF', 'XLSX', 'PPTX', 'HTML', 'DOCX'].map((f) => (
            <button key={f} className="cok-dl-btn" onClick={() => handleDownload(f)}>Download {f}</button>
          ))}
        </div>
      </div>

      {/* Overview */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Overview</div>
        <div className="cok-kpis">
          <div className="cok-kpi"><div className="cok-kpi-lbl">Suppliers</div><div className="cok-kpi-val">{k.suppliers ?? '—'}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">Dosage forms</div><div className="cok-kpi-val">{k.dosageForms ?? '—'}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">Molecule type</div><div className="cok-kpi-val" style={{ fontSize: 20 }}>{k.moleculeType ? `${k.moleculeType} molecule` : '—'}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">NL available</div><div className={`cok-kpi-val ${yn(k.nlAvailable)}`} style={{ fontSize: 22 }}>{k.nlAvailable ?? '—'}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">NL MA availability</div><div className={`cok-kpi-val ${yn(k.nlMA)}`} style={{ fontSize: 22 }}>{k.nlMA ?? '—'}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">EU availability</div><div className={`cok-kpi-val ${yn(k.euAvailable)}`} style={{ fontSize: 22 }}>{k.euAvailable ?? '—'}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">Switch list</div><div className={`cok-kpi-val ${k.switch === 'red' ? 'red' : k.switch === 'orange' ? 'orange' : 'green'}`} style={{ fontSize: 22, textTransform: 'capitalize' }}>{k.switch ?? '—'}</div></div>
          <div className="cok-kpi"><div className="cok-kpi-lbl">Contracts</div><div className="cok-kpi-val">{k.contracts ?? '—'}</div></div>
        </div>
      </div>

      {/* Summary */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Summary</div>
        <div className="cok-muted" style={{ marginBottom: 6 }}>
          <strong style={{ color: 'var(--navy)' }}>{molecule}</strong> · ATC Code: {k.atc?.code || '—'} · ATC Category: {k.atc?.category || '—'} · ATC Classification: {k.atc?.classification || '—'}
        </div>
        <AiBlock title="Capability" text={ai?.capability} loading={aiStatus !== 'ready'} />
        <AiBlock title="Executive summary" text={ai?.executiveSummary} loading={aiStatus !== 'ready'} />
        <div className="cok-muted" style={{ marginTop: 8, fontSize: 11 }}>AI-generated via web search — verify against primary sources.</div>
      </div>

      {/* Synonyms */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Synonyms</div>
        <div className="cok-two">
          <div>
            {aiStatus !== 'ready' ? <div className="cok-muted">Loading…</div>
              : (ai?.synonyms && ai.synonyms.length)
                ? ai.synonyms.map((s, i) => <span key={i} className="cok-chip">{cap(s)}</span>)
                : <div className="cok-muted">No synonyms found.</div>}
          </div>
          <div className="cok-chart-card">
            <h4>Market presence</h4>
            <MiniBar data={[
              { name: 'Suppliers', value: k.suppliers || 0 },
              { name: 'Dosage forms', value: k.dosageForms || 0 },
              { name: 'NL reg (CBG)', value: k.cbgCount || 0 },
              { name: 'EU reg (HMA)', value: k.hmaCount || 0 },
              { name: 'Contracts', value: k.contracts || 0 },
            ]} />
          </div>
        </div>
      </div>

      {/* Route of administration */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Route of administration</div>
        <div className="cok-two">
          <div>
            {aiStatus !== 'ready' ? <div className="cok-muted">Loading…</div>
              : (ai?.routeOfAdministration && ai.routeOfAdministration.length)
                ? (<>
                    {ai.routeOfAdministration.map((s, i) => {
                      const active = selRoute && selRoute.toLowerCase() === String(s).toLowerCase();
                      return (
                        <span key={i} className="cok-chip" onClick={() => setSelRoute(active ? null : s)}
                          style={{ cursor: 'pointer', background: active ? 'var(--teal)' : undefined, color: active ? '#fff' : undefined, borderColor: active ? 'var(--teal)' : undefined }}>
                          {cap(s)}
                        </span>
                      );
                    })}
                    <div className="cok-muted" style={{ marginTop: 8, fontSize: 11 }}>{selRoute ? `Showing dosage forms for “${cap(selRoute)}” — click again to clear.` : 'Click a route to filter the dosage forms.'}</div>
                  </>)
                : <div className="cok-muted">Not available.</div>}
          </div>
          <div className="cok-chart-card">
            <h4>Dosage forms on the market{selRoute ? ` — ${cap(selRoute)}` : ''}</h4>
            <MiniBar data={(k.dosageFormCounts || []).filter((d) => !selRoute || routeMatch(selRoute, d.name)).map((d) => ({ name: titleCase(d.name), value: d.value }))} />
          </div>
        </div>
      </div>

      {/* Pharmaceutical forms */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Pharmaceutical forms</div>

        {/* molecule-level clinical & regulatory */}
        <div className="cok-charts" style={{ marginBottom: 16 }}>
          <div className="cok-chart-card">
            <h4>Critical medicine list</h4>
            {aiStatus !== 'ready' ? <div className="cok-muted">Loading…</div> : (
              <div>
                <span className={`cok-badge ${ai?.criticalMedicine?.status === 'Yes' ? 'red' : ai?.criticalMedicine?.status === 'No' ? 'green' : ''}`}>{ai?.criticalMedicine?.status || 'Unknown'}</span>
                {ai?.criticalMedicine?.note ? <div className="cok-muted" style={{ marginTop: 6 }}>{ai.criticalMedicine.note}</div> : null}
              </div>
            )}
            <h4 style={{ marginTop: 14 }}>Patent</h4>
            {forms?.patent ? <div className="cok-muted">{forms.patent.status || '—'}{forms.patent.expiry ? ` · expiry ${forms.patent.expiry}` : ''}</div> : <div className="cok-muted">No patent data.</div>}
            <h4 style={{ marginTop: 14 }}>Registered suppliers ({forms?.supplierCount || 0})</h4>
            <div>{(forms?.suppliers || []).slice(0, 20).map((s, i) => <span key={i} className="cok-chip">{s}</span>)}{(!forms || !forms.suppliers?.length) && <span className="cok-muted">None.</span>}</div>
          </div>
          <div className="cok-chart-card">
            <h4>Indications &amp; place in guidelines</h4>
            <Html html={ai?.indications} loading={aiStatus !== 'ready'} />
            <h4 style={{ marginTop: 14 }}>Special information</h4>
            <Html html={ai?.specialInformation} loading={aiStatus !== 'ready'} empty="No special information." />
          </div>
        </div>

        {/* Sub Section-1: drill-down Group -> dosage form -> strength */}
        {!tree ? <div className="cok-muted">Loading forms…</div> : (tree.groups || []).length === 0 ? <div className="cok-muted">No pharmaceutical forms found.</div> : (
          <div style={{ marginBottom: 12 }}>
            <div className="cok-muted" style={{ marginBottom: 4, fontWeight: 600 }}>Group</div>
            <div style={{ marginBottom: 8 }}>
              {(tree.groups || []).map((g) => { const active = selGroup === g.group; return (
                <span key={g.group} className="cok-chip" onClick={() => { setSelGroup(active ? null : g.group); setSelForm(null); setSelStrength(null); setSelPack(null); }}
                  style={{ cursor: 'pointer', background: active ? 'var(--teal)' : undefined, color: active ? '#fff' : undefined, borderColor: active ? 'var(--teal)' : undefined }}>{g.group} ({g.forms.length})</span>
              ); })}
            </div>
            {selGroup && (() => { const g = (tree.groups || []).find((x) => x.group === selGroup); const forms = (g && g.forms) || []; return (
              <><div className="cok-muted" style={{ marginBottom: 4, fontWeight: 600 }}>Dosage form</div>
                <div style={{ marginBottom: 8 }}>{forms.map((f) => { const active = selForm === f.form; return (
                  <span key={f.form} className="cok-chip" onClick={() => { setSelForm(active ? null : f.form); setSelStrength(null); setSelPack(null); }}
                    style={{ cursor: 'pointer', background: active ? 'var(--teal)' : undefined, color: active ? '#fff' : undefined, borderColor: active ? 'var(--teal)' : undefined }}>{titleCase(f.form)} ({f.strengths.length})</span>
                ); })}</div></>
            ); })()}
            {selGroup && selForm && (() => { const g = (tree.groups || []).find((x) => x.group === selGroup); const f = g && g.forms.find((x) => x.form === selForm); const strengths = (f && f.strengths) || []; return (
              <><div className="cok-muted" style={{ marginBottom: 4, fontWeight: 600 }}>Strength</div>
                <div style={{ marginBottom: 8 }}>{strengths.map((s) => { const active = selStrength === s.strength; return (
                  <span key={s.strength + (s.unit || '')} className="cok-chip" onClick={() => { setSelStrength(active ? null : s.strength); setSelPack(null); }}
                    style={{ cursor: 'pointer', background: active ? 'var(--teal)' : undefined, color: active ? '#fff' : undefined, borderColor: active ? 'var(--teal)' : undefined }}>{`${s.strength} ${s.unit || ''}`.trim()}</span>
                ); })}</div></>
            ); })()}
            {selGroup && selForm && selStrength && (() => { const g = (tree.groups || []).find((x) => x.group === selGroup); const f = g && g.forms.find((x) => x.form === selForm); const s = f && f.strengths.find((x) => x.strength === selStrength); const packs = (s && s.packs) || []; return packs.length ? (
              <><div className="cok-muted" style={{ marginBottom: 4, fontWeight: 600 }}>Pack size</div>
                <div style={{ marginBottom: 8 }}>{packs.slice().sort((a, b) => (Number(a) || 0) - (Number(b) || 0)).map((p) => { const active = selPack === p; return (
                  <span key={p} className="cok-chip" onClick={() => setSelPack(active ? null : p)}
                    style={{ cursor: 'pointer', background: active ? 'var(--teal)' : undefined, color: active ? '#fff' : undefined, borderColor: active ? 'var(--teal)' : undefined }}>{p}</span>
                ); })}</div></>
            ) : null; })()}
            <div className="cok-muted" style={{ fontSize: 11 }}>Showing: <strong>{selGroup || 'All groups'}</strong>{selForm ? ` › ${titleCase(selForm)}` : ''}{selStrength ? ` › ${selStrength}` : ''}{selPack ? ` › pack ${selPack}` : ''}. Not clicking a level means &ldquo;all&rdquo;.</div>
          </div>
        )}

        {/* Cards for the current selection */}
        {(() => { const a = analytics || {}; return (
          <>
            <div className="cok-kpis" style={{ marginBottom: 12 }}>
              <div className="cok-kpi"><div className="cok-kpi-lbl">Market value</div><div className="cok-kpi-val" style={{ fontSize: 19 }}>{analyticsLoading ? '…' : eur(a.marketValue)}</div></div>
              <div className="cok-kpi"><div className="cok-kpi-lbl">Market volume</div><div className="cok-kpi-val" style={{ fontSize: 19 }}>{analyticsLoading ? '…' : num(a.marketVolume)}</div></div>
              <div className="cok-kpi"><div className="cok-kpi-lbl">Highest AIP</div><div className="cok-kpi-val" style={{ fontSize: 19 }}>{a.highestAIP != null ? eur(a.highestAIP) : '—'}</div></div>
              <div className="cok-kpi"><div className="cok-kpi-lbl">Lowest AIP</div><div className="cok-kpi-val" style={{ fontSize: 19 }}>{a.lowestAIP != null ? eur(a.lowestAIP) : '—'}</div></div>
            </div>
            <div className="cok-two">
              <div>
                <KV rows={[
                  { k: 'Market value (current)', v: eur(a.marketValue) },
                  { k: 'Market volume (current)', v: num(a.marketVolume) },
                  { k: 'Retail vs Hospital', v: a.retailPct != null ? `${a.retailPct}% / ${a.hospitalPct}%` : '—' },
                  { k: 'Highest AIP', v: a.highestAIP != null ? eur(a.highestAIP) : '—' },
                  { k: 'Lowest AIP', v: a.lowestAIP != null ? eur(a.lowestAIP) : '—' },
                  { k: 'Reimbursed (Vergoed)', v: selForm ? (a.reimbursed || 'Unknown') : '—' },
                  { k: 'Reimbursement price (GVS limit)', v: (selForm && a.gvsLimit != null) ? eur(a.gvsLimit) : '—' },
                  { k: 'Shortages (Farmanco)', v: comingSoon },
                  { k: 'Critical medicine list', v: aiStatus === 'ready' ? (ai?.criticalMedicine?.status || 'Unknown') : '…' },
                  { k: 'Preferentiestatus CZ', v: prefName('CZ') },
                  { k: 'Preferentiestatus Menzis', v: prefName('Menzis') },
                  { k: 'Preferentiestatus VGZ', v: prefName('VGZ') },
                  { k: 'Preferentiestatus Zilveren Kruis', v: prefName('Zilveren Kruis') },
                  { k: 'Preferentiestatus ONVZ', v: prefName('ONVZ') },
                  { k: 'Preferentiestatus DSW', v: prefName('DSW') },
                ]} />
                <h4 style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 12.5, margin: '12px 0 6px' }}>Competitors — Z‑Index ({a.competitorsZindexCount || 0})</h4>
                <div>{(a.competitorsZindex || []).slice(0, 30).map((c, i) => <span key={i} className="cok-chip">{c}</span>)}{(!a.competitorsZindex || !a.competitorsZindex.length) && <span className="cok-muted">None.</span>}</div>
                <h4 style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 12.5, margin: '12px 0 6px' }}>Competitors — FarmInfo ({a.competitorsFarminfoCount || 0})</h4>
                <div>{(a.competitorsFarminfo || []).slice(0, 30).map((c, i) => <span key={i} className="cok-chip">{c}</span>)}{(!a.competitorsFarminfo || !a.competitorsFarminfo.length) && <span className="cok-muted">None.</span>}</div>
              </div>
              <div className="cok-chart-card">
                <h4>Retail vs Hospital (%)</h4>
                <MiniBar data={[{ name: 'Retail %', value: a.retailPct || 0 }, { name: 'Hospital %', value: a.hospitalPct || 0 }]} height={190} />
                <h4 style={{ marginTop: 8 }}>Retail / Hospital share</h4>
                <PieMini data={[{ name: 'Retail', value: a.retailPct || 0 }, { name: 'Hospital', value: a.hospitalPct || 0 }]} colors={['#1A8AAF', '#0D2B3E']} height={200} />
              </div>
            </div>
          </>
        ); })()}
      </div>

      {/* Current-year MACH data */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />MACH data — current year{selForm ? ` · ${titleCase(selForm)}` : ''}{selStrength ? ` · ${selStrength}` : ''}{selPack ? ` · pack ${selPack}` : ''}</div>
        {!mach ? <div className="cok-muted">Loading MACH data…</div>
          : (mach.rows || []).length === 0 ? <div className="cok-muted">No MACH data for this selection.</div>
            : (
              <div className="cok-table-wrap" style={{ maxHeight: 460, overflowY: 'auto' }}>
                <table className="cok-table">
                  <thead><tr><th>Form</th><th>Strength</th><th>Pack</th><th>PSF value (€)</th><th>CU</th><th>Unit</th><th>DOT</th></tr></thead>
                  <tbody>
                    {mach.rows.map((r, i) => (
                      <tr key={i}>
                        <td>{titleCase(r.form)}</td>
                        <td>{`${r.strength} ${r.unit || ''}`.trim()}</td>
                        <td>{r.pack || '—'}</td>
                        <td>{r.psf != null ? eur(r.psf) : '—'}</td>
                        <td>{r.cu != null ? num(r.cu) : '—'}</td>
                        <td>{r.unitv != null ? num(r.unitv) : '—'}</td>
                        <td>{r.dot != null ? num(r.dot) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>

      {/* Historical data */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Historical data (MAT by year){selStrength ? ` · ${selStrength}` : ''}{selPack ? ` · pack ${selPack}` : ''}</div>
        {!hist ? <div className="cok-muted">Loading historical data…</div>
          : (hist.years || []).length === 0 ? <div className="cok-muted">No historical data found.</div>
            : (<>
              <div className="cok-chart-card" style={{ marginBottom: 12 }}>
                <h4>PSF total market value (€) by MAT year</h4>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={hist.years} margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={70} /><Tooltip contentStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="psf" name="PSF (€)" stroke="#1A8AAF" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="cok-table-wrap" style={{ overflowX: 'auto' }}>
                <table className="cok-table">
                  <thead><tr><th>Year (MAT)</th><th>PSF value (€)</th><th>CU</th><th>Unit</th><th>DOT</th></tr></thead>
                  <tbody>
                    {[...hist.years].sort((a, b) => b.year.localeCompare(a.year)).map((y, i) => (
                      <tr key={i}>
                        <td>{y.year}</td>
                        <td>{y.psf != null ? eur(y.psf) : '—'}</td>
                        <td>{y.cu != null ? num(y.cu) : '—'}</td>
                        <td>{y.unit != null ? num(y.unit) : '—'}</td>
                        <td>{y.dot != null ? num(y.dot) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>)}
      </div>

      {/* Competitor Analysis — Farminform vs Z-Index */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Competitor Analysis{selForm ? ` · ${titleCase(selForm)}` : ''}{selStrength ? ` · ${selStrength}` : ''}{selPack ? ` · pack ${selPack}` : ''}</div>
        {!competitor ? <div className="cok-muted">Loading competitor analysis…</div>
          : (() => {
            const c = competitor; const p = c.price || {};
            const dirColor = p.direction === 'up' ? 'var(--green)' : p.direction === 'down' ? 'var(--red)' : 'var(--t2)';
            const dirArrow = p.direction === 'up' ? '▲' : p.direction === 'down' ? '▼' : '▬';
            const dirLabel = p.direction === 'up' ? 'Increasing' : p.direction === 'down' ? 'Decreasing' : p.direction === 'flat' ? 'Stable' : '—';
            return (<>
              <div className="cok-muted" style={{ marginBottom: 10 }}>Comparing Farminform current-year sales (<strong>{c.farminformCount || 0}</strong> companies) against Z-Index preference policy (<strong>{c.zindexCount || 0}</strong> companies) for the current selection.</div>

              <div className="cok-kpis" style={{ marginBottom: 12 }}>
                <div className="cok-kpi"><div className="cok-kpi-lbl">New MAs (Farminform only)</div><div className="cok-kpi-val" style={{ fontSize: 19 }}>{c.newMAsCount || 0}</div></div>
                <div className="cok-kpi"><div className="cok-kpi-lbl">Removed MAs (Z-Index only)</div><div className="cok-kpi-val" style={{ fontSize: 19 }}>{c.removedMAsCount || 0}</div></div>
                <div className="cok-kpi"><div className="cok-kpi-lbl">In both sources</div><div className="cok-kpi-val" style={{ fontSize: 19 }}>{c.commonCount || 0}</div></div>
                <div className="cok-kpi"><div className="cok-kpi-lbl">Price trend</div><div className="cok-kpi-val" style={{ fontSize: 19, color: dirColor }}>{dirArrow} {dirLabel}</div></div>
              </div>

              <div className="cok-two">
                <div>
                  <h4 style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 12.5, margin: '0 0 6px' }}>New records / new MAs ({c.newMAsCount || 0})</h4>
                  <div className="cok-muted" style={{ fontSize: 11, marginBottom: 6 }}>Selling in Farminform but not in Z-Index preference policy — newly active / not yet listed.</div>
                  <div style={{ marginBottom: 12 }}>{(c.newMAs || []).map((x, i) => <span key={i} className="cok-chip" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}>{x}</span>)}{(!c.newMAs || !c.newMAs.length) && <span className="cok-muted">None.</span>}</div>

                  <h4 style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 12.5, margin: '0 0 6px' }}>Cancelled records / removed MAs ({c.removedMAsCount || 0})</h4>
                  <div className="cok-muted" style={{ fontSize: 11, marginBottom: 6 }}>In Z-Index preference policy but no current Farminform sales — likely exited.</div>
                  <div style={{ marginBottom: 12 }}>{(c.removedMAs || []).map((x, i) => <span key={i} className="cok-chip" style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>{x}</span>)}{(!c.removedMAs || !c.removedMAs.length) && <span className="cok-muted">None.</span>}</div>

                  <h4 style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 12.5, margin: '0 0 6px' }}>Present in both ({c.commonCount || 0})</h4>
                  <div>{(c.common || []).slice(0, 40).map((x, i) => <span key={i} className="cok-chip">{x}</span>)}{(!c.common || !c.common.length) && <span className="cok-muted">None.</span>}</div>
                </div>

                <div>
                  <KV rows={[
                    { k: 'Price / unit — current', v: p.current != null ? eur(p.current) : '—' },
                    { k: 'Price / unit — previous', v: p.previous != null ? eur(p.previous) : '—' },
                    { k: 'Change', v: p.pctChange != null ? `${p.pctChange > 0 ? '+' : ''}${p.pctChange}%` : '—' },
                    { k: 'Direction', v: dirLabel },
                    { k: 'Tender position', v: comingSoon },
                    { k: 'Tender vs non-tender', v: comingSoon },
                  ]} />
                  <div className="cok-chart-card" style={{ marginTop: 12 }}>
                    <h4>Price / unit — previous vs current (€)</h4>
                    <MiniBar data={[{ name: 'Previous', value: p.previous || 0 }, { name: 'Current', value: p.current || 0 }]} height={190} />
                  </div>
                </div>
              </div>
              <div className="cok-muted" style={{ fontSize: 11, marginTop: 8 }}>New/removed determined by comparing Farminform <em>Deelnemer</em> vs Z-Index <em>Naam_productverantwoordelijke</em>. Price direction from Farminform price/unit (omzet ÷ afzet), current vs previous MAT period.</div>
            </>);
          })()}
      </div>

      {/* Supplier Data — full supplier product DB, scoped to this molecule */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />Supplier Data</div>
        <div className="cok-muted" style={{ marginBottom: 10 }}>Your supplier product database, filtered to <strong>{titleCase(molecule)}</strong>. Same view, filters and controls as the Supplier Products page.</div>
        <SupplierProducts embedded lockedInn={molecule} />
      </div>

      {/* CBG Data — medicines registration board records */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />CBG Data</div>
        {!cbg ? <div className="cok-muted">Loading CBG records…</div>
          : (cbg.rows || []).length === 0 ? <div className="cok-muted">No CBG registered products for this molecule.</div>
            : (<>
              <div className="cok-muted" style={{ marginBottom: 8 }}>{cbg.total || cbg.rows.length} registered product{(cbg.total || cbg.rows.length) !== 1 ? 's' : ''} for &ldquo;{titleCase(molecule)}&rdquo;{cbg.total > cbg.rows.length ? ` (showing first ${cbg.rows.length})` : ''}.</div>
              <div className="cok-table-wrap" style={{ maxHeight: 460, overflowY: 'auto' }}>
                <table className="cok-table">
                  <thead><tr><th>MA number</th><th>Product</th><th>ATC</th><th>Active substance</th></tr></thead>
                  <tbody>
                    {cbg.rows.map((r, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }}>{r.ma_number || '—'}</td>
                        <td>{r.product_name || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{r.atc || '—'}</td>
                        <td>{r.active_substance || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="cok-muted" style={{ fontSize: 11, marginTop: 8 }}>Source: CBG / MEB medicines register (College ter Beoordeling van Geneesmiddelen).</div>
            </>)}
      </div>

      {/* FK Data — Farmacotherapeutisch Kompas records */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />FK Data</div>
        {!fk ? <div className="cok-muted">Loading FK records…</div>
          : fk.blocked ? (
            <div style={{ padding: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, color: '#92400e', fontSize: 12.5 }}>
              FK is currently unavailable from the server. <a href={`https://www.farmacotherapeutischkompas.nl/zoeken?alledomeinen_zoekterm=${encodeURIComponent(molecule)}&domein=geneesmiddelen`} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', fontWeight: 600 }}>Search &ldquo;{titleCase(molecule)}&rdquo; directly on FK</a>.
            </div>
          ) : (fk.results || []).length === 0 ? <div className="cok-muted">No Farmacotherapeutisch Kompas records for this molecule.</div>
            : (<>
              <div className="cok-muted" style={{ marginBottom: 8 }}>{fk.results.length} FK record{fk.results.length !== 1 ? 's' : ''} matching &ldquo;{titleCase(molecule)}&rdquo;. Click a medicine to expand its details.</div>
              <div className="cok-table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
                <table className="cok-table">
                  <thead><tr><th style={{ width: 40 }}>#</th><th>Medicine</th><th style={{ width: 90 }}>Source</th></tr></thead>
                  <tbody>
                    {fk.results.map((r, i) => (
                      <tr key={i}>
                        <td style={{ color: 'var(--t3)' }}>{i + 1}</td>
                        <td>
                          <button type="button" onClick={() => openFkDetail(r.fkPath)}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit', textAlign: 'left', fontWeight: 600 }}>
                            {r.name}{fkDetailPath === r.fkPath ? ' ▲' : ' ▼'}
                          </button>
                        </td>
                        <td><a href={r.fkUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)', textDecoration: 'none', fontWeight: 600 }}>FK ↗</a></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Inline detail for the selected FK record */}
              {(fkDetailLoading || fkDetail || fkDetailError || fkDetailBlocked) && (
                <div style={{ marginTop: 12 }}>
                  {fkDetailLoading && (
                    <div style={{ padding: 28, textAlign: 'center', color: 'var(--t3)', fontSize: 12.5, background: 'var(--card)', borderRadius: 10, border: '1px solid #eef3f7' }}>
                      <div style={{ fontWeight: 600 }}>Loading…</div>
                    </div>
                  )}
                  {fkDetailBlocked && (
                    <div style={{ padding: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, color: '#92400e', fontSize: 12.5 }}>
                      FK detail page requires a headless browser; the server could not fetch it directly.
                    </div>
                  )}
                  {fkDetailError && (
                    <div style={{ padding: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 12.5 }}>Failed to load detail: {fkDetailError}</div>
                  )}
                  {fkDetail && (() => { const shown = (fkLang === 'en' && fkEn) ? fkEn : fkDetail; const isEn = fkLang === 'en' && !!fkEn; return (
                    <div style={{ background: 'var(--card)', borderRadius: 12, border: '1px solid #eef3f7', overflow: 'hidden' }}>
                      <style>{`
.fk-section-body { font-size: 13px; color: var(--t1); line-height: 1.65; }
.fk-section-body ul { margin: 4px 0 4px 18px; padding: 0; }
.fk-section-body li { margin: 3px 0; }
.fk-section-body p { margin: 6px 0; }
.fk-section-body a { color: #1d4ed8; }
.fk-section-body table { border-collapse: collapse; margin: 8px 0; font-size: 12px; width: 100%; }
.fk-section-body td, .fk-section-body th { border: 1px solid #dee2e6; padding: 5px 8px; text-align: left; }
.fk-section-body th { background: #f8fafc; font-weight: 600; }
.fk-section-body strong, .fk-section-body b { font-weight: 600; }
                      `}</style>
                      <div style={{ padding: '10px 16px', background: '#fffbeb', borderBottom: '1px solid #fde68a', fontSize: 11.5, color: '#92400e', lineHeight: 1.5 }}>
                        {isEn
                          ? <>Machine-translated from the Dutch Farmacotherapeutisch Kompas. <strong>Verify dosing and clinical details against the source.</strong>{' '}</>
                          : <>Original Dutch from the Farmacotherapeutisch Kompas.{' '}</>}
                        <a href={fkDetail.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', fontWeight: 600 }}>Open on FK</a>
                      </div>
                      <div style={{ padding: '10px 16px', borderBottom: '1px solid #eef3f7', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <h4 style={{ margin: 0, fontWeight: 700, color: 'var(--t1)', fontSize: 15 }}>{shown.title_en || shown.title}</h4>
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                          {isEn
                            ? <button type="button" onClick={() => setFkLang('nl')} style={{ fontSize: 11.5, color: 'var(--teal)', background: 'none', border: '1px solid var(--teal)', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Show original (Dutch)</button>
                            : <button type="button" onClick={translateFkToEnglish} disabled={fkTranslating} style={{ fontSize: 11.5, color: '#fff', background: fkTranslating ? '#9cc6d6' : 'var(--teal)', border: 'none', borderRadius: 5, padding: '4px 11px', cursor: fkTranslating ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>{fkTranslating ? 'Translating…' : 'Translate to English'}</button>}
                          <button type="button" onClick={closeFkDetail} style={{ fontSize: 11.5, color: 'var(--teal)', background: 'none', border: '1px solid var(--teal)', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Close</button>
                        </div>
                      </div>
                      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {(shown.sections || []).map((s, i) => {
                          const isOpen = !!fkOpenSections[i];
                          const heading = s.heading_en || s.heading || `Section ${i + 1}`;
                          const bodyHtml = s.body_html_en || s.body || '';
                          return (
                            <div key={i} style={{ borderBottom: '1px solid #eef3f7' }}>
                              <button type="button" onClick={() => toggleFkSection(i)}
                                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: isOpen ? '#f0fdf4' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--teal)' }}>{heading}</span>
                                <span style={{ fontSize: 11, color: 'var(--t3)', flexShrink: 0, marginLeft: 8 }}>{isOpen ? '▲' : '▼'}</span>
                              </button>
                              {isOpen && (
                                <div style={{ padding: '8px 16px 14px' }}>
                                  {s.note && <div style={{ fontSize: 11, color: '#92400e', marginBottom: 6, fontStyle: 'italic' }}>{s.note}</div>}
                                  <div className="fk-section-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bodyHtml, { ADD_ATTR: ['target'] }) }} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ); })()}
                </div>
              )}
              <div className="cok-muted" style={{ fontSize: 11, marginTop: 8 }}>Source: Farmacotherapeutisch Kompas (farmacotherapeutischkompas.nl).</div>
            </>)}
      </div>

      {/* HMA Data — MRI Product Index (EU mutual-recognition/decentralised) */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />HMA Data</div>
        {!hma ? <div className="cok-muted">Loading HMA records…</div>
          : (hma.rows || []).length === 0 ? <div className="cok-muted">No HMA MRI records for this molecule.</div>
            : (<>
              <div className="cok-muted" style={{ marginBottom: 8 }}>{hma.total || hma.rows.length} MRI product{(hma.total || hma.rows.length) !== 1 ? 's' : ''} for &ldquo;{titleCase(molecule)}&rdquo;.</div>
              <div className="cok-table-wrap" style={{ maxHeight: 460, overflowY: 'auto' }}>
                <table className="cok-table">
                  <thead><tr><th>Product number</th><th>RMS</th><th>CMS</th><th>Product</th><th>Active substances</th><th>Dose forms</th><th>MAH</th><th>ATC</th><th>Outcome</th></tr></thead>
                  <tbody>
                    {hma.rows.map((r, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }}>{r.product_link ? <a href={r.product_link} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', fontWeight: 600 }}>{r.product_number || '—'}</a> : (r.product_number || '—')}</td>
                        <td>{r.rms || '—'}</td>
                        <td>{r.cms || '—'}</td>
                        <td>{r.product_name || '—'}</td>
                        <td>{r.active_substances || '—'}</td>
                        <td>{r.dose_forms || '—'}</td>
                        <td>{r.mah || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{r.atc_code || '—'}</td>
                        <td>{r.outcome || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="cok-muted" style={{ fontSize: 11, marginTop: 8 }}>Source: HMA MRI Product Index (hma.eu).</div>
            </>)}
      </div>

      {/* NL market — preference policy (at the end) */}
      <div className="cok-sec">
        <div className="cok-sec-title"><span className="cok-dot" />NL market — preference policy</div>
        {!pref ? <div className="cok-muted">Loading…</div>
          : !pref.available ? <div className="cok-muted">No NL preference‑policy products found for this molecule.</div>
            : (<>
              <div className="cok-muted" style={{ marginBottom: 8 }}>{pref.count} NL products · {pref.responsibleCount} product responsibles. <strong style={{ color: 'var(--green)' }}>Preferent</strong> = insurer's preferred product · <strong style={{ color: 'var(--amber)' }}>Non‑preferent</strong> = not preferred · <strong>–</strong> = no policy.</div>
              <div style={{ marginBottom: 12 }}>{(pref.responsibles || []).slice(0, 30).map((r, i) => <span key={i} className="cok-chip">{r}</span>)}</div>
              <div className="cok-chart-card" style={{ marginBottom: 12 }}>
                <h4>Preference status by insurer</h4>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={(pref.insurers || []).map((ins) => ({ name: ins.label, Preferent: pref.summary[ins.key]?.preferent || 0, 'Non-preferent': pref.summary[ins.key]?.nonPreferent || 0, 'No policy': pref.summary[ins.key]?.noPolicy || 0 }))} margin={{ top: 4, right: 8, bottom: 4, left: -14 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef3f6" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip contentStyle={{ fontSize: 12 }} /><Legend />
                    <Bar dataKey="Preferent" stackId="a" fill="#1D8A5E" />
                    <Bar dataKey="Non-preferent" stackId="a" fill="#D98323" />
                    <Bar dataKey="No policy" stackId="a" fill="#6B8299" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <input className="cok-in" placeholder="Filter product or responsible…" value={prefQ} onChange={(e) => setPrefQ(e.target.value)}
                style={{ width: '100%', maxWidth: 340, border: '1.5px solid var(--brd)', borderRadius: 8, padding: '7px 11px', fontSize: 12.5, marginBottom: 8, background: 'var(--surf)', color: 'var(--t1)', outline: 'none' }} />
              <div className="cok-table-wrap" style={{ maxHeight: 480, overflowY: 'auto' }}>
                <table className="cok-table">
                  <thead><tr><th>Product</th><th>Responsible</th><th>Pack</th>{(pref.insurers || []).map((ins) => <th key={ins.key}>{ins.label}</th>)}</tr></thead>
                  <tbody>
                    {(pref.products || []).filter((p) => { const qq = prefQ.toLowerCase(); return !qq || String(p.article || '').toLowerCase().includes(qq) || String(p.responsible || '').toLowerCase().includes(qq); }).slice(0, 150).map((p, i) => (
                      <tr key={i}>
                        <td>{p.article}</td><td>{p.responsible || '—'}</td><td>{p.pack || '—'}</td>
                        {(pref.insurers || []).map((ins) => { const v = p.status[ins.key]; return <td key={ins.key}>{v === '1' ? <span className="cok-badge green">Preferent</span> : v === '2' ? <span className="cok-badge orange">Non‑pref</span> : <span className="cok-muted">–</span>}</td>; })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>)}
      </div>
    </div>
   </>
  );
}
